import React, { useState } from 'react';
import { getTenantToday } from '../../utils/dateUtils';
import axios from 'axios';
import {
  PortcoModalShell,
  ModalBody,
  FormGrid,
  FormField,
  FieldLabel,
  TextInput,
  SelectInput,
  ErrorBanner,
  ModalFoot,
  PrimaryBtn,
  SecondaryBtn,
} from './PortcoModalShell';

import { DateField } from '../shared/DateField';

type MarkType = 'DCF' | 'PUBLIC_COMP' | 'GP';

interface Props {
  portcoId: number;
  portcoName: string;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Mark a portfolio company at a specific valuation type. Wraps the three
 * existing PortcoNAVService endpoints:
 *   - POST /api/portco/{id}/marks/dcf
 *   - POST /api/portco/{id}/marks/public-comp
 *   - POST /api/portco/{id}/marks/gp
 *
 * The mark-type selector toggles which fields render so the operator only
 * sees the inputs relevant to the chosen valuation method. All marks land in
 * fact_portco_valuation; PortcoNAVService computes equity / EV / multiples
 * downstream.
 */
const PortcoMarkModal: React.FC<Props> = ({ portcoId, portcoName, onClose, onSuccess }) => {
  const today = getTenantToday();
  const [markType, setMarkType] = useState<MarkType>('DCF');
  const [markDate, setMarkDate] = useState<string>(today);

  // DCF inputs
  const [growth, setGrowth] = useState<string>('0.05');
  const [discount, setDiscount] = useState<string>('0.12');
  const [exitMultiple, setExitMultiple] = useState<string>('8.0');
  const [years, setYears] = useState<string>('5');

  // Public-comp inputs
  const [evEbitda, setEvEbitda] = useState<string>('');
  const [evRevenue, setEvRevenue] = useState<string>('');

  // GP-mark inputs
  const [equityValue, setEquityValue] = useState<string>('');
  const [enterpriseValue, setEnterpriseValue] = useState<string>('');

  // Shared
  const [rationale, setRationale] = useState<string>('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      if (markType === 'DCF') {
        const g = Number(growth);
        const d = Number(discount);
        const ex = Number(exitMultiple);
        const yrs = Number(years);
        if (!Number.isFinite(g) || !Number.isFinite(d) || !Number.isFinite(ex) || !Number.isFinite(yrs)) {
          throw new Error('All DCF inputs must be numeric.');
        }
        await axios.post(`/api/portco/${portcoId}/marks/dcf`, {
          markDate,
          ebitdaGrowthRate: g,
          discountRate: d,
          exitMultiple: ex,
          years: yrs,
          rationale: rationale || undefined,
        });
      } else if (markType === 'PUBLIC_COMP') {
        const e = Number(evEbitda);
        const r = Number(evRevenue);
        if ((!evEbitda && !evRevenue) || (evEbitda && !Number.isFinite(e)) || (evRevenue && !Number.isFinite(r))) {
          throw new Error('Provide at least one valid multiple (EV/EBITDA or EV/Revenue).');
        }
        await axios.post(`/api/portco/${portcoId}/marks/public-comp`, {
          markDate,
          evEbitdaMultiple: evEbitda ? e : undefined,
          evRevenueMultiple: evRevenue ? r : undefined,
          rationale: rationale || undefined,
        });
      } else {
        const eq = Number(equityValue);
        const ev = Number(enterpriseValue);
        if (!Number.isFinite(eq) || eq < 0) {
          throw new Error('Equity value must be a non-negative number.');
        }
        if (enterpriseValue && !Number.isFinite(ev)) {
          throw new Error('Enterprise value must be numeric if supplied.');
        }
        await axios.post(`/api/portco/${portcoId}/marks/gp`, {
          markDate,
          equityValue: eq,
          enterpriseValue: enterpriseValue ? ev : undefined,
          rationale: rationale || undefined,
        });
      }
      onSuccess();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Mark failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortcoModalShell
      title="Record valuation mark"
      subtitle={portcoName}
      onClose={onClose}
    >
      <ModalBody>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}

        <FormGrid $cols={2}>
          <FormField>
            <FieldLabel>Mark type</FieldLabel>
            <SelectInput
              value={markType}
              onChange={(e) => setMarkType(e.target.value as MarkType)}
            >
              <option value="DCF">DCF</option>
              <option value="PUBLIC_COMP">Public comp</option>
              <option value="GP">GP mark</option>
            </SelectInput>
          </FormField>
          <FormField>
            <FieldLabel>Mark date</FieldLabel>
            <DateField
              value={markDate ?? ''}
              onChange={iso => setMarkDate(iso)}
            />
          </FormField>

          {markType === 'DCF' && (
            <>
              <FormField>
                <FieldLabel>EBITDA growth (decimal)</FieldLabel>
                <TextInput
                  type="number"
                  step="0.01"
                  value={growth}
                  onChange={(e) => setGrowth(e.target.value)}
                  placeholder="0.05"
                />
              </FormField>
              <FormField>
                <FieldLabel>Discount rate (decimal)</FieldLabel>
                <TextInput
                  type="number"
                  step="0.01"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                  placeholder="0.12"
                />
              </FormField>
              <FormField>
                <FieldLabel>Exit multiple (×EBITDA)</FieldLabel>
                <TextInput
                  type="number"
                  step="0.1"
                  value={exitMultiple}
                  onChange={(e) => setExitMultiple(e.target.value)}
                  placeholder="8.0"
                />
              </FormField>
              <FormField>
                <FieldLabel>Forecast years</FieldLabel>
                <TextInput
                  type="number"
                  step="1"
                  min="1"
                  max="10"
                  value={years}
                  onChange={(e) => setYears(e.target.value)}
                />
              </FormField>
            </>
          )}

          {markType === 'PUBLIC_COMP' && (
            <>
              <FormField>
                <FieldLabel>EV / EBITDA multiple</FieldLabel>
                <TextInput
                  type="number"
                  step="0.1"
                  placeholder="e.g. 9.5"
                  value={evEbitda}
                  onChange={(e) => setEvEbitda(e.target.value)}
                />
              </FormField>
              <FormField>
                <FieldLabel>EV / Revenue multiple</FieldLabel>
                <TextInput
                  type="number"
                  step="0.1"
                  placeholder="e.g. 2.4"
                  value={evRevenue}
                  onChange={(e) => setEvRevenue(e.target.value)}
                />
              </FormField>
            </>
          )}

          {markType === 'GP' && (
            <>
              <FormField>
                <FieldLabel>Equity value</FieldLabel>
                <TextInput
                  type="number"
                  step="0.01"
                  placeholder="e.g. 250000000"
                  value={equityValue}
                  onChange={(e) => setEquityValue(e.target.value)}
                />
              </FormField>
              <FormField>
                <FieldLabel>Enterprise value (optional)</FieldLabel>
                <TextInput
                  type="number"
                  step="0.01"
                  placeholder="e.g. 320000000"
                  value={enterpriseValue}
                  onChange={(e) => setEnterpriseValue(e.target.value)}
                />
              </FormField>
            </>
          )}

          <FormField $span={2}>
            <FieldLabel>Rationale (optional)</FieldLabel>
            <TextInput
              type="text"
              placeholder="Audit trail note — why this mark, what changed"
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
            />
          </FormField>
        </FormGrid>
      </ModalBody>

      <ModalFoot>
        <SecondaryBtn type="button" onClick={onClose} disabled={submitting}>
          Cancel
        </SecondaryBtn>
        <PrimaryBtn type="button" onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Record mark'}
        </PrimaryBtn>
      </ModalFoot>
    </PortcoModalShell>
  );
};

export default PortcoMarkModal;
